import { MelodyLib } from '@tunasong/music-lib'
import type { Chord } from '@tunasong/music-lib'
import type { MelodyNote, Scale } from '@tunasong/schemas'
import type { FC } from 'react'
import { useTheoryCopy } from '../theory-copy/use-theory-copy.js'

export interface FeedbackStripProps {
  scale: Scale
  chords: Chord[]
  notes: MelodyNote[]
}

const BEATS_PER_CHORD = 4

const chordAtPosition = (chords: Chord[], position: number): Chord => {
  const idx = Math.min(Math.floor(position / BEATS_PER_CHORD), chords.length - 1)
  return chords[idx]
}

/**
 * Per-note classification readout. One row per placed note, listing pitch,
 * octave, beat position, and the lexicon-driven classification label.
 *
 * Each row uses `useTheoryCopy` so the register matches the user's
 * `theoryLevel` (mirrors Phase 1's pattern — no string paraphrasing in the
 * component). When `theoryLevel === 'off'` rows fall back to the
 * lexicon's `displayNameShort` so the user still sees a label.
 */
export const FeedbackStrip: FC<FeedbackStripProps> = props => {
  const { scale, chords, notes } = props
  if (notes.length === 0) {
    return null
  }
  return (
    <ul>
      {notes.map((note, idx) => {
        const chord = chordAtPosition(chords, note.position)
        const classification = MelodyLib.classifyMelodyNote({ note, chord, scale })
        return (
          <FeedbackRow
            key={`${note.position}-${note.pitch}-${idx}`}
            note={note}
            lexiconId={classification.lexiconId}
          />
        )
      })}
    </ul>
  )
}

interface FeedbackRowProps {
  note: MelodyNote
  lexiconId: string
}

const FeedbackRow: FC<FeedbackRowProps> = props => {
  const { note, lexiconId } = props
  const copy = useTheoryCopy(lexiconId)
  const label = copy?.tag ?? copy?.displayNameShort ?? lexiconId
  return (
    <li>
      <span>
        {note.pitch}
        {note.octave}
      </span>
      <span> (beat {note.position}) </span>
      <span>— {label}</span>
    </li>
  )
}
